import { useEffect, useState } from 'react';
import { CircleDot, Clock, Coins, Send, UserRound } from 'lucide-react';
import { Badge, Button, Input, Switch, Textarea, cn } from '@respectly/ui';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type { ChatAvailabilityConfigDto, ChatBrandDto } from '@respectly/shared-types';
import { useChatWidgetsStore } from '../../stores/useChatWidgetsStore';
import type { ChatBrandDraft } from '../../stores/useChatWidgetsStore';
import { Field } from './PresentationSection';

const DAYS = [
  { key: '1', label: 'Mon' },
  { key: '2', label: 'Tue' },
  { key: '3', label: 'Wed' },
  { key: '4', label: 'Thu' },
  { key: '5', label: 'Fri' },
  { key: '6', label: 'Sat' },
  { key: '0', label: 'Sun' },
];

const DEFAULT_TIMEZONE = 'America/New_York';
const DEFAULT_WINDOW = '09:00-17:00';

/**
 * Accept what someone actually has to hand.
 *
 * "Get link to channel" in Teams yields a long URL with the id percent-encoded
 * inside it. Requiring a human to find and decode that segment is asking them to
 * do a job a regex does perfectly, and getting it subtly wrong produces a widget
 * that silently pages nobody.
 */
function extractChannelId(raw: string): string {
  const value = raw.trim();
  if (!value) return '';
  const decoded = (() => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  })();
  const match = decoded.match(/19:[^/?#\s]+@thread\.[a-z0-9]+/i);
  return match ? match[0] : value;
}

/**
 * How the widget behaves: brand-specific prompt additions, whether a person can
 * be reached, when, and how much it may spend in a day.
 */
export function BehaviourSection({
  brand,
  draft,
  edit,
}: {
  brand: ChatBrandDto;
  draft: ChatBrandDraft;
  edit: (patch: ChatBrandDraft) => void;
}) {
  const features = draft.features ?? brand.features;
  const handoffEnabled = features.handoff !== false;
  const availability = draft.availability !== undefined ? draft.availability : brand.availability;
  const budget = draft.dailyTokenBudget !== undefined ? draft.dailyTokenBudget : brand.dailyTokenBudget;
  const promptAppend =
    draft.systemPromptAppend !== undefined ? draft.systemPromptAppend : brand.systemPromptAppend;
  const businessName =
    draft.businessName !== undefined ? draft.businessName : brand.businessName;
  const businessSummary =
    draft.businessSummary !== undefined ? draft.businessSummary : brand.businessSummary;
  const basePrompt = draft.systemPrompt !== undefined ? draft.systemPrompt : brand.systemPrompt;
  const defaultPrompt = useChatWidgetsStore((s) => s.defaultPrompt);
  const supportEmail = draft.supportEmail !== undefined ? draft.supportEmail : brand.supportEmail;
  const tenantId = draft.tenantId !== undefined ? draft.tenantId : brand.tenantId;

  const teamsConfig = draft.teamsConfig !== undefined ? draft.teamsConfig : brand.teamsConfig;
  const teamsServiceUrl = String(teamsConfig?.serviceUrl ?? '');
  const teamsChannelId = String(teamsConfig?.channelId ?? '');

  /**
   * Both halves are held together: the server rejects a half-set block, so
   * clearing one field has to leave the other editable rather than collapsing
   * the object.
   */
  const setTeams = (key: 'serviceUrl' | 'channelId', raw: string) => {
    const next = { serviceUrl: teamsServiceUrl, channelId: teamsChannelId, [key]: raw };
    if (key === 'channelId') next.channelId = extractChannelId(raw);
    if (!next.serviceUrl && !next.channelId) {
      edit({ teamsConfig: null });
      return;
    }
    edit({ teamsConfig: next });
  };

  const setWindows = (dayKey: string, raw: string) => {
    const base: ChatAvailabilityConfigDto = availability ?? {
      timezone: DEFAULT_TIMEZONE,
      windows: {},
    };
    const windows = { ...base.windows };
    const entries = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (entries.length) windows[dayKey] = entries;
    else delete windows[dayKey];
    edit({ availability: { ...base, windows } });
  };

  return (
    <div className="space-y-8">
      <Field
        label="Owner"
        hint="The account this widget belongs to. Leave it unowned for Respectly's own sites: an owned widget's conversations also appear in that account's inbox, where their staff can answer them."
      >
        <OwnerPicker
          value={tenantId ?? null}
          currentName={brand.tenantName}
          onChange={(next) => edit({ tenantId: next })}
        />
      </Field>

      <Field
        label="Business name"
        hint="Who the assistant says it works for. Leave it blank to use the widget's own name. This matters on a site that is not ours: without it the assistant introduces itself as Respectly."
      >
        <Input
          value={businessName ?? ''}
          maxLength={200}
          onChange={(e) => edit({ businessName: e.target.value || null })}
          placeholder={brand.displayName}
        />
      </Field>

      <Field
        label="What this business does"
        hint="One paragraph, in plain words. It is the only description of the business the assistant has — leave it blank and it will not characterise the business at all, answering from the documents only."
      >
        <Textarea
          value={businessSummary ?? ''}
          rows={4}
          maxLength={4000}
          onChange={(e) => edit({ businessSummary: e.target.value || null })}
          placeholder="AORForms is …"
        />
      </Field>

      <Field
        label="Base prompt"
        hint="Empty means this widget uses the shared prompt, which is where improvements to the assistant's behaviour arrive. Give it one of its own only when this site needs to be handled differently — and edit the default rather than starting from nothing, so the rules that matter survive."
      >
        <Textarea
          value={basePrompt ?? ''}
          rows={12}
          maxLength={20000}
          className="font-mono text-xs"
          onChange={(e) => edit({ systemPrompt: e.target.value || null })}
          placeholder={
            defaultPrompt
              ? 'Using the shared prompt. Start from it to write one for this widget.'
              : 'Using the shared prompt.'
          }
        />
        <div className="flex items-center gap-2">
          {basePrompt === null || basePrompt === '' ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!defaultPrompt}
              onClick={() => edit({ systemPrompt: defaultPrompt })}
            >
              Start from the shared prompt
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => edit({ systemPrompt: null })}
            >
              Go back to the shared prompt
            </Button>
          )}
          <p className="text-[11px] text-slate-400">
            Keep the SOURCES line instruction — the widget parses it out of the reply to build the
            citation strip.
          </p>
        </div>
      </Field>

      <Field
        label="Prompt addition"
        hint="Appended to the shared system prompt for this widget only. Use it for audience and tone — “this visitor is already a customer” — not to grant capabilities the assistant does not have."
      >
        <Textarea
          value={promptAppend ?? ''}
          rows={5}
          maxLength={8000}
          onChange={(e) => edit({ systemPromptAppend: e.target.value || null })}
          placeholder="This visitor is already a customer, signed in to the app. Assume familiarity with the basics."
        />
        <p className="text-[11px] text-slate-400">
          Added on top of whichever base prompt this widget uses. The model, and the shared prompt
          for every widget without one of its own, are governed centrally in AI Operations under{' '}
          <span className="font-mono">public.website-expert</span>.
        </p>
      </Field>

      <Field label="Support email" hint="Offered when the assistant cannot help and nobody is on chat.">
        <Input
          type="email"
          value={supportEmail ?? ''}
          maxLength={255}
          onChange={(e) => edit({ supportEmail: e.target.value || null })}
          placeholder="support@respectly.ai"
        />
      </Field>

      {/* ── Handoff ─────────────────────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex gap-3">
            <UserRound className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Talk to a person
              </p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                Lets the assistant escalate to a colleague. With this off it points visitors at the
                support address instead, and never implies someone is coming.
              </p>
            </div>
          </div>
          <Switch
            checked={handoffEnabled}
            onCheckedChange={(checked) => edit({ features: { ...features, handoff: checked } })}
            aria-label="Enable human handoff"
          />
        </div>

        {handoffEnabled && (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                <Clock className="h-4 w-4 text-slate-400" />
                When someone is on chat
              </p>
              <AvailabilityBadge state={brand.agentAvailability} />
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Outside these hours the assistant asks for an email address instead of promising a
              person. An unparseable or empty schedule counts as <strong>nobody available</strong> —
              it fails closed, because collecting an email is recoverable and a broken promise is
              not.
            </p>

            <div className="flex items-center gap-2">
              <label className="w-24 shrink-0 text-xs font-medium text-slate-600 dark:text-slate-300">
                Timezone
              </label>
              <Input
                value={availability?.timezone ?? ''}
                onChange={(e) =>
                  edit({
                    availability: {
                      timezone: e.target.value,
                      windows: availability?.windows ?? {},
                    },
                  })
                }
                placeholder={DEFAULT_TIMEZONE}
                className="max-w-xs font-mono text-xs"
              />
            </div>

            <div className="space-y-1.5">
              {DAYS.map((day) => {
                const windows = availability?.windows?.[day.key] ?? [];
                return (
                  <div key={day.key} className="flex items-center gap-2">
                    <label className="w-24 shrink-0 text-xs font-medium text-slate-600 dark:text-slate-300">
                      {day.label}
                    </label>
                    <Input
                      value={windows.join(', ')}
                      onChange={(e) => setWindows(day.key, e.target.value)}
                      placeholder="closed"
                      className="max-w-xs font-mono text-xs"
                    />
                    {windows.length === 0 && (
                      <span className="text-[11px] text-slate-400">nobody on chat</span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400">
              Use <span className="font-mono">09:00-17:00</span>, comma-separated for a split day.
              An overnight shift needs two entries — <span className="font-mono">22:00-24:00</span>{' '}
              on one day and <span className="font-mono">00:00-06:00</span> on the next.
            </p>

            {availability && (
              <button
                type="button"
                onClick={() => edit({ availability: null })}
                className="text-xs font-medium text-slate-500 underline-offset-2 hover:underline dark:text-slate-400"
              >
                Clear the schedule (nobody ever on chat)
              </button>
            )}
            {!availability && (
              <button
                type="button"
                onClick={() =>
                  edit({
                    availability: {
                      timezone: DEFAULT_TIMEZONE,
                      windows: { '1': [DEFAULT_WINDOW], '2': [DEFAULT_WINDOW], '3': [DEFAULT_WINDOW], '4': [DEFAULT_WINDOW], '5': [DEFAULT_WINDOW] },
                    },
                  })
                }
                className="text-xs font-medium text-sky-600 underline-offset-2 hover:underline dark:text-sky-400"
              >
                Add a weekday schedule
              </button>
            )}
          </div>
        )}

        {handoffEnabled && (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
              <Send className="h-4 w-4 text-slate-400" />
              Where the handoff is posted
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              The Teams channel that gets a card when a visitor asks for a person.
              Leave it empty and the escalation is still recorded and still
              answerable in <strong>Live Chats</strong> — it just pages nobody.
              Both fields are required; one on its own is ignored.
            </p>

            <div className="flex items-center gap-2">
              <label className="w-24 shrink-0 text-xs font-medium text-slate-600 dark:text-slate-300">
                Service URL
              </label>
              <Input
                value={teamsServiceUrl}
                onChange={(e) => setTeams('serviceUrl', e.target.value)}
                placeholder="https://smba.trafficmanager.net/amer/"
                className="font-mono text-xs"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-24 shrink-0 text-xs font-medium text-slate-600 dark:text-slate-300">
                Channel ID
              </label>
              <Input
                value={teamsChannelId}
                onChange={(e) => setTeams('channelId', e.target.value)}
                placeholder="19:….@thread.tacv2"
                className="font-mono text-xs"
              />
            </div>
            <p className="text-[11px] text-slate-400">
              In Teams: channel <span className="font-mono">⋯</span> → <em>Get link to
              channel</em>. The id is the <span className="font-mono">19%3a…%40thread.tacv2</span>{' '}
              segment of that URL, URL-decoded to{' '}
              <span className="font-mono">19:…@thread.tacv2</span>. Pasting the whole
              link works too — it is decoded and trimmed on save.
            </p>
            {(teamsServiceUrl || teamsChannelId) && (
              <button
                type="button"
                onClick={() => edit({ teamsConfig: null })}
                className="text-xs font-medium text-slate-500 underline-offset-2 hover:underline dark:text-slate-400"
              >
                Clear the channel (record escalations, page nobody)
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Budget ──────────────────────────────────────────────────────────── */}
      <Field
        label="Daily token budget"
        hint="A ceiling on model tokens per day for this widget. Rate limits bound requests; this bounds money — which is what matters when an anonymous endpoint gets found."
      >
        <div className="flex items-center gap-2">
          <Coins className="h-4 w-4 shrink-0 text-slate-400" />
          <Input
            type="number"
            min={0}
            value={budget ?? ''}
            onChange={(e) =>
              edit({ dailyTokenBudget: e.target.value === '' ? null : Number(e.target.value) })
            }
            placeholder="No limit"
            className="max-w-[180px] tabular-nums"
          />
          <span className="text-xs text-slate-400">tokens per day</span>
        </div>
        <p className="text-[11px] text-slate-400">
          Counted per widget in Redis and reset at UTC midnight, so one busy page cannot silence
          the others. Once spent, the widget tells visitors to email instead. Reported spend lives
          in AI Operations.
        </p>
      </Field>
    </div>
  );
}

export function AvailabilityBadge({ state }: { state: ChatBrandDto['agentAvailability'] }) {
  const meta = {
    available: { label: 'Someone is on chat', color: 'text-emerald-600 dark:text-emerald-400' },
    offline: { label: 'Nobody on chat now', color: 'text-slate-500 dark:text-slate-400' },
    disabled: { label: 'Handoff off', color: 'text-slate-400' },
  }[state];

  return (
    <Badge variant="secondary" className={cn('gap-1.5 text-[11px]', meta.color)}>
      <CircleDot className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
}

/**
 * Which account owns this widget.
 *
 * A plain select over every account rather than a search dialog: the list is
 * small, the choice is made once when a widget is set up, and "unowned" has to be
 * the visibly available option rather than something you reach by clearing a
 * field. Loaded lazily on mount so the Chat Widgets page does not pay for it
 * until the Behaviour tab is opened.
 */
function OwnerPicker({
  value,
  currentName,
  onChange,
}: {
  value: string | null;
  currentName: string | null;
  onChange: (tenantId: string | null) => void;
}) {
  const [accounts, setAccounts] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const api = getApiClient();
        const res = await api.get<{ data: Array<{ id: string; name: string }> }>(
          `${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.LIST}?page=1&limit=500`
        );
        if (!cancelled) setAccounts(res?.data ?? []);
      } catch {
        if (!cancelled) setAccounts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the saved owner selectable even if the account list has not arrived or
  // the account falls outside the page we fetched — otherwise opening this tab
  // and saving would silently clear an owner nobody meant to touch.
  const options =
    value && currentName && !accounts.some((a) => a.id === value)
      ? [{ id: value, name: currentName }, ...accounts]
      : accounts;

  return (
    <>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
      >
        <option value="">Respectly (no account)</option>
        {options.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
      {loading && <p className="text-[11px] text-slate-400">Loading accounts&hellip;</p>}
    </>
  );
}
